import React, { useEffect, useMemo, useState } from "react";
import BoostHero from "./BoostHero";
import IdeaCard from "./IdeaCard";
import FiltersModal from "./FiltersModal";
import DetailsDrawer from "./DetailsDrawer";
import { Search } from "lucide-react";
import { pickApiUrl, statusToKey, toNumber, parseDate } from "./utils";

import data from "../../../../_data/boost-innovation.json";

export default function BoostInnovation() {
  const hero = data?.hero;
  const ui = data?.ui || {};
  const api = data?.api || {};
  const statusTabs = data?.statusTabs || [
    "All",
    "Submitted",
    "In Review",
    "Approved",
    "In Development",
    "Completed",
    "Denied",
  ];

  const PAGE_SIZE = 6;
  const [page, setPage] = useState(1);

  const [ideas, setIdeas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [activeTab, setActiveTab] = useState("All");
  const [labelQuery, setLabelQuery] = useState("");

  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    categories: new Set(),
    departments: new Set(),
    startDate: "",
    endDate: "",
  });
  const [draft, setDraft] = useState(filters);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    const run = async () => {
      try {
        setLoading(true);
        setErr(null);

        const url = pickApiUrl(api);
        const res = await fetch(url);
        if (!res.ok) throw new Error(`API error ${res.status}`);

        const raw = await res.json();

        const mapped = (raw || [])
          .filter((x) => {
            const t = String(x?.Title || "");
            const d = String(x?.Description || "");
            return /automation/i.test(t) || /automation/i.test(d);
          })
          .map((x) => {
            const submittedAt = x["Submitted Date"] || x.Submitted || "";
            const statusKey = statusToKey(x.Status);

            return {
              ...x,
              _submittedAt: submittedAt,
              _statusKey: statusKey,
              _visits: toNumber(x.Visits),
              _likes: toNumber(x.Likes),
            };
          })
          .sort((a, b) => {
            const ta = parseDate(a._submittedAt)?.getTime() || 0;
            const tb = parseDate(b._submittedAt)?.getTime() || 0;
            return tb - ta;
          });

        setIdeas(mapped);
      } catch (e) {
        setErr(e?.message || "Failed to load ideas.");
      } finally {
        setLoading(false);
      }
    };

    run();
  }, []);

  const categories = useMemo(() => {
    return Array.from(
      new Set(ideas.map((i) => String(i.Category || "").trim()).filter((v) => v && v !== "—")),
    ).sort();
  }, [ideas]);

  const departments = useMemo(() => {
    return Array.from(
      new Set(ideas.map((i) => String(i.Department || "").trim()).filter((v) => v && v !== "—")),
    ).sort();
  }, [ideas]);

  const counts = useMemo(() => {
    const base = {};
    statusTabs.forEach((t) => (base[t] = 0));
    base.All = ideas.length;

    ideas.forEach((i) => {
      const k = statusTabs.includes(i._statusKey) ? i._statusKey : "Submitted";
      base[k] = (base[k] || 0) + 1;
    });

    return base;
  }, [ideas, statusTabs]);

  const filtered = useMemo(() => {
    const q = labelQuery.trim().toLowerCase();
    const start = filters.startDate ? new Date(filters.startDate) : null;
    const end = filters.endDate ? new Date(filters.endDate) : null;

    return ideas.filter((idea) => {
      if (activeTab !== "All" && idea._statusKey !== activeTab) return false;

      if (filters.categories.size && !filters.categories.has(idea.Category)) return false;
      if (filters.departments.size && !filters.departments.has(idea.Department)) return false;

      if (start || end) {
        const d = parseDate(idea._submittedAt);
        if (!d) return false;

        if (start) {
          const s = new Date(start);
          s.setHours(0, 0, 0, 0);
          if (d < s) return false;
        }

        if (end) {
          const e = new Date(end);
          e.setHours(23, 59, 59, 999);
          if (d > e) return false;
        }
      }

      if (!q) return true;

      const hay = [
        idea.Title,
        idea.Description,
        idea.Category,
        idea.Department,
        idea["Submitter name"],
        idea["Manager name"],
        idea["Director name"],
      ]
        .join(" ")
        .toLowerCase();

      return hay.includes(q);
    });
  }, [ideas, activeTab, filters, labelQuery]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, labelQuery, filters]);

  const totalPages = useMemo(() => {
    return Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  }, [filtered.length]);

  const paged = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, page]);

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const hasActiveFilters =
    filters.categories.size || filters.departments.size || !!filters.startDate || !!filters.endDate;

  const removeCategory = (v) => {
    setFilters((prev) => {
      const next = {
        ...prev,
        categories: new Set(prev.categories),
        departments: new Set(prev.departments),
      };
      next.categories.delete(v);
      return next;
    });
  };

  const removeDepartment = (v) => {
    setFilters((prev) => {
      const next = {
        ...prev,
        categories: new Set(prev.categories),
        departments: new Set(prev.departments),
      };
      next.departments.delete(v);
      return next;
    });
  };

  const clearAllFilters = () => {
    setFilters({ categories: new Set(), departments: new Set(), startDate: "", endDate: "" });
  };

  const openFilters = () => {
    setDraft({
      categories: new Set(filters.categories),
      departments: new Set(filters.departments),
      startDate: filters.startDate,
      endDate: filters.endDate,
    });
    setFiltersOpen(true);
  };

  const applyFilters = () => {
    setFilters({
      categories: new Set(draft.categories),
      departments: new Set(draft.departments),
      startDate: draft.startDate,
      endDate: draft.endDate,
    });
    setFiltersOpen(false);
  };

  const clearDraft = () => {
    setDraft({ categories: new Set(), departments: new Set(), startDate: "", endDate: "" });
  };

  const openIdea = (idea) => {
    setSelected(idea);
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelected(null);
  };

  if (!hero || !ui) {
    return (
      <section className="bi">
        <div className="bi-empty">{ui?.emptyData || "Boost Innovation data not found."}</div>
      </section>
    );
  }

  return (
    <section className="bi" aria-label="Boost Innovation">
      <BoostHero hero={hero} />

      <div className="bi-card">
        <div className="bi-cardHeader">
          <div className="bi-cardTopRow">
            <h2 className="bi-h2">{ui.ideasTitle}</h2>

            <div className="bi-actions">
              <div className="bi-search">
                <Search className="bi-searchIcon" aria-hidden="true" />
                <input
                  value={labelQuery}
                  onChange={(e) => setLabelQuery(e.target.value)}
                  placeholder={ui.searchPlaceholder}
                />
              </div>

              <button type="button" className="bi-advBtn" onClick={openFilters}>
                {ui.advancedFilters}
              </button>
            </div>
          </div>

          {hasActiveFilters ? (
            <div className="bi-activeFilters">
              <div className="bi-activeTitle">Active filters</div>

              <div className="bi-activeRow">
                {[...filters.categories].map((c) => (
                  <button
                    key={`cat-${c}`}
                    type="button"
                    className="bi-chip"
                    onClick={() => removeCategory(c)}
                    title="Remove filter"
                  >
                    {c}
                    <span className="bi-chipX" aria-hidden="true">
                      ×
                    </span>
                  </button>
                ))}

                {[...filters.departments].map((d) => (
                  <button
                    key={`dep-${d}`}
                    type="button"
                    className="bi-chip"
                    onClick={() => removeDepartment(d)}
                    title="Remove filter"
                  >
                    {d}
                    <span className="bi-chipX" aria-hidden="true">
                      ×
                    </span>
                  </button>
                ))}

                {filters.startDate ? (
                  <button
                    type="button"
                    className="bi-chip"
                    onClick={() => setFilters((p) => ({ ...p, startDate: "" }))}
                    title="Remove filter"
                  >
                    From: {filters.startDate}
                    <span className="bi-chipX" aria-hidden="true">
                      ×
                    </span>
                  </button>
                ) : null}

                {filters.endDate ? (
                  <button
                    type="button"
                    className="bi-chip"
                    onClick={() => setFilters((p) => ({ ...p, endDate: "" }))}
                    title="Remove filter"
                  >
                    To: {filters.endDate}
                    <span className="bi-chipX" aria-hidden="true">
                      ×
                    </span>
                  </button>
                ) : null}

                <button type="button" className="bi-clearFilters" onClick={clearAllFilters}>
                  Clear all filters
                </button>
              </div>
            </div>
          ) : null}

          <div className="bi-tabs" role="tablist" aria-label="Idea status tabs">
            {statusTabs.map((t) => {
              const active = activeTab === t;
              return (
                <button
                  key={t}
                  type="button"
                  className={`bi-tab ${active ? "is-active" : ""}`}
                  onClick={() => setActiveTab(t)}
                >
                  {t}
                  <span className="bi-tabCount">{counts[t] ?? 0}</span>
                  {active ? <span className="bi-tabUnderline" /> : null}
                </button>
              );
            })}
          </div>
        </div>

        <div className="bi-list" key={activeTab}>
          {loading ? (
            <div className="bi-empty">Loading…</div>
          ) : err ? (
            <div className="bi-empty">{err}</div>
          ) : filtered.length === 0 ? (
            <div className="bi-empty">{ui.emptyList}</div>
          ) : (
            paged.map((idea) => (
              <IdeaCard key={`${idea.ID}-${idea._statusKey}`} idea={idea} onOpen={openIdea} />
            ))
          )}
        </div>

        {!loading && !err && totalPages > 1 ? (
          <div className="bi-pager">
            <button
              type="button"
              className="bi-pagerBtn"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Prev
            </button>

            <div className="bi-pagerInfo">
              Page <strong>{page}</strong> of <strong>{totalPages}</strong>
            </div>

            <button
              type="button"
              className="bi-pagerBtn"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >
              Next
            </button>
          </div>
        ) : null}
      </div>

      {filtersOpen ? (
        <FiltersModal
          ui={ui}
          categories={categories}
          departments={departments}
          draft={draft}
          setDraft={setDraft}
          onClose={() => setFiltersOpen(false)}
          onApply={applyFilters}
          onClear={clearDraft}
        />
      ) : null}

      <DetailsDrawer
        open={drawerOpen}
        uiDetails={data?.details}
        idea={selected}
        onClose={closeDrawer}
      />
    </section>
  );
}
